import { put, takeEvery, select } from 'redux-saga/effects'

import {
  LOCATION_FETCH_FAIL,
  LOCATION_FETCHING,
  LOCATION_RESET,
  LOCATION_SET,
  LOCATION_PERMISSION_STATUS_SET,
  ON_SUBMIT_TIP,
  SET_TIP_DATA,
  TIP_DATA_RESET,
  TIP_SUBMIT_FAIL,
  TIP_SUBMIT_ATTEMPT,
  TIP_SUBMIT_SUCCESS,
  UPDATE_LOADING
} from '@redux/actions/types'

import { submitTip } from '@actions/tips'

import { LOCATION_STATUS } from '@utils/helpers'

import { toast } from 'react-toastify'

const getAnalytics = state => state.analytic.analytics

const getCurrentOSUser = state => state.location.electronOSUser

const getAuth = (state) => ({
  apiKey: state.auth.apiKey,
  hash: state.auth.hash,
  subscriberId: state.auth.subscriberId
})

const getIsLoading = state => state.ui.isLoading

const getLocation = state => state.location

const getTip = state => state.tip

/**
 * Logs analytics events for the tip submission and dispatches the API submit action.
 * @param action
 */
function * onSubmitTip(action) {
  const Analytics = yield select(getAnalytics)
  Analytics.logEvent('TipSubmitted')

  const { auth, data: tipData } = action.payload
  const { location, sendAnonymous } = tipData


  if (sendAnonymous) {
    if (location) {
      Analytics.logEvent('TipSubmittedAnonymousWithLocation')
    } else {
      Analytics.logEvent('TipSubmittedAnonymousWithoutLocation')
    }
  } else if (location) {
    Analytics.logEvent('TipSubmittedNonAnonymousWithLocation')
  } else {
    Analytics.logEvent('TipSubmittedNonAnonymousWithoutLocation')
  }
  const currentOSUser = yield select(getCurrentOSUser)

  const data = {
    ...tipData,
    current_os_user: currentOSUser
  }

  yield put(submitTip({ auth, data }))
}

/**
 * Prepares tip data and either submits immediately or waits for location fetch.
 */
function * onAttemptTipSubmit() {
  yield put({
    type: UPDATE_LOADING,
    payload: {
      loadingType: TIP_SUBMIT_ATTEMPT,
      loadingAction: 'set',
      meta: null
    }
  })

  const auth = yield select(getAuth)
  const isLoading = yield select(getIsLoading)
  const location = yield select(getLocation)
  const tip = yield select(getTip)

  const { anonymous, category, configHash, fileUploads, isStandalone, message, scenarioId, sendLocation, type } = tip
  const { currentLocation } = location

  const locationData = currentLocation ? {
    lat: currentLocation.latitude,
    lng: currentLocation.longitude
  } : null

  const data = {
    anonymous,
    category,
    configHash,
    fileKeys: fileUploads.map(({ fileId }) => fileId),
    isStandalone,
    location: sendLocation ? locationData : null,
    message: message,
    scenario_id: scenarioId,
    subscriber_id: auth.subscriberId,
    type
  }

  if (sendLocation && currentLocation === null) {
    yield put({
      type: SET_TIP_DATA,
      payload: {
        isAwaitingLocation: true
      }
    })

    const isLoadingLocation = isLoading.some(
      item => item.loadingType === LOCATION_FETCHING
    )

    if (!isLoadingLocation) {
      yield put({ type: LOCATION_FETCHING })
    }
  } else {
    yield put({
      type: ON_SUBMIT_TIP,
      payload: { auth, data }
    })
  }
}

/**
 * Clears the tip submit loading state.
 */
function * unsetTipLoading() {
  yield put({
    type: UPDATE_LOADING,
    payload: {
      loadingType: TIP_SUBMIT_ATTEMPT,
      loadingAction: 'unset',
      meta: null
    }
  })
}

/**
 * Clears tip loading state when location fetch fails.
 */
function * onLocationFetchFail() {
  yield unsetTipLoading()
}

/**
 * Enables send-location on the tip and re-attempts submission if awaiting location.
 */
function * onLocationSet() {
  const tip = yield select(getTip)
  const { isAwaitingLocation } = tip

  if (!tip) return

  yield put({
    type: SET_TIP_DATA,
    payload: {
      sendLocation: true
    }
  })

  if (isAwaitingLocation) {
    yield onAttemptTipSubmit()
  }
}

/**
 * Automatically enables send-location on the tip when geolocation permission is granted.
 * @param action
 */
function * onSetLocationPermissionStatusSet(action) {
  const { payload: hasPermission } = action

  if (hasPermission === LOCATION_STATUS.GRANTED) {
    yield put({
      type: SET_TIP_DATA,
      payload: {
        sendLocation: true
      }
    })
  }
}


/**
 * Clears loading state, resets tip data, and resets location after a successful tip submit.
 */
function * onTipSubmitSuccess() {
  yield unsetTipLoading()

  yield put({ type: TIP_DATA_RESET })
  yield put({ type: LOCATION_RESET })
}

/**
 * Clears loading state and shows a warning toast when tip submission fails.
 */
function * onTipSubmitFail() {
  yield unsetTipLoading()

  toast.warn('Error submitting tip')
}


/**
 * Root saga that watches for tip-related actions.
 */
function * watchTips() {
  yield takeEvery(LOCATION_FETCH_FAIL, onLocationFetchFail)
  yield takeEvery(LOCATION_PERMISSION_STATUS_SET, onSetLocationPermissionStatusSet)
  yield takeEvery(LOCATION_SET, onLocationSet)
  yield takeEvery(ON_SUBMIT_TIP, onSubmitTip)
  yield takeEvery(TIP_SUBMIT_ATTEMPT, onAttemptTipSubmit)
  yield takeEvery(TIP_SUBMIT_FAIL, onTipSubmitFail)
  yield takeEvery(TIP_SUBMIT_SUCCESS, onTipSubmitSuccess)
}
export default watchTips
